<template>
    <div class="group ds-schedule-day-cell cursor-pointer" :class="cellHighlightClass" :data-schedule-day="day">
        <div v-if="usesCellHighlighting && hasMajorTimeDeviation && authStore.permissions?.extended_cell_info" class="absolute top-[2px] right-[2px] h-1.5 w-1.5 rounded-full bg-warning"></div>
        <div v-if="timesheet && workDay" class="group h-full text-gray text-[11px] flex flex-col items-center justify-center">
            <div>{{ main }}</div>
            <div v-if="additional">{{ additional }}</div>
            <div v-else>-</div>
            <div class="border-b border-graylight w-6"></div>
            <div>{{ fact }}</div>
            <InfoTooltip :fact :night :nightMain :nightAdditional :main :additional :schedule :day :additional-time="timesheet.additional_seconds" :timesheet />
        </div>
        <div v-else-if="timesheet" class="h-full text-gray text-[11px] flex flex-col items-center justify-center">
            <div>{{ dayType }}</div>
            <div v-if="main">{{ main }}</div>
            <div v-else>-</div>
            <div class="border-b border-graylight w-6"></div>
            <div v-if="fact">{{ fact }}</div>
            <div v-else>-</div>
            <InfoTooltip :fact :night :nightMain :nightAdditional :main :additional :schedule :day :additional-time="timesheet.additional_seconds" :timesheet />
        </div>
        <div v-else class="h-full text-gray text-[11px] flex flex-col items-center justify-center">
            <div>ВЫХ</div>
            <div>-</div>
            <div class="border-b border-graylight w-6"></div>
            <div>-</div>
        </div>
        <div class="h-[3px] absolute bottom-0 w-full" v-if="usesCellHighlighting || (dayColor && authStore.permissions?.extended_cell_info)" :class="usesCellHighlighting ? cellUnderlineClass : dayColor"></div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Schedule } from '@/types'
import dayjs from "dayjs"
import duration from "dayjs/plugin/duration"
import utc from "dayjs/plugin/utc"
import InfoTooltip from './InfoTooltip.vue'
import { useScheduleStore } from '@/stores/scheduleStore'
import { useAuthStore } from '@/stores/authStore'
interface Props {
    schedule: Schedule,
    day: number
}

dayjs.extend(duration)
dayjs.extend(utc)

const store = useScheduleStore()
const authStore = useAuthStore()

const { schedule, day } = defineProps<Props>()
const days = computed(() => store.timesheets.find(ts => ts.employment_id === schedule.id)?.timesheet?.days)
const timesheet = computed(() => getTsheet(day, days.value))

function getTsheet(day:any, days: any) {
    for(let key in days) {
        if(+key === day) return days[key]
    }
}
const main = computed(() => dayjs.duration(timesheet.value.totals.main_seconds, 'seconds').format('HH:mm'))
const fact = computed(() => dayjs.duration(timesheet.value.totals.fact_seconds, 'seconds').format('HH:mm'))
const night = computed(() => timesheet.value.totals.fact_night_seconds ? dayjs.duration(timesheet.value.totals.fact_night_seconds, 'seconds').format('HH:mm') : timesheet.value.totals.fact_night_seconds)
const nightMain = computed(() => timesheet.value.totals.main_night_seconds ? dayjs.duration(timesheet.value.totals.main_night_seconds, 'seconds').format('HH:mm') : timesheet.value.totals.main_night_seconds)
const nightAdditional = computed(() => timesheet.value.totals.additional_night_seconds ? dayjs.duration(timesheet.value.totals.additional_night_seconds, 'seconds').format('HH:mm') : timesheet.value.totals.additional_night_seconds)
const additional = computed(() => dayjs.duration(timesheet.value.totals.additional_seconds, 'seconds').format('HH:mm'))
const workDay = computed(() => timesheet.value?.shifts[0].day_type_code === 'WD')
const dayType = computed(() => store.dayTypes.find(item => item.code === timesheet.value.day_type_code)?.short_name )

const shifts = computed(() => schedule.shifts.filter(shift => +dayjs(shift.date).format('D') === day))
const plannedShifts = computed(() => shifts.value.filter(shift => shift.plan_work_start_at && shift.plan_work_end_at))
const isFutureDay = computed(() => dayjs(store.currentScheduleDate).date(day).isAfter(dayjs(), 'day'))
const cellUnderlineStatus = computed(() => {
    if (plannedShifts.value.every(shift => !shift.fact_work_start_at && !shift.fact_work_end_at)) {
        return isFutureDay.value ? '' : 'blue'
    }
    const hasRedCondition = plannedShifts.value.some((shift) => {
        if (!shift.fact_work_start_at || !shift.fact_work_end_at) return true
        const lateStart = dayjs.utc(shift.fact_work_start_at).diff(dayjs.utc(shift.plan_work_start_at), 'minute')
        const earlyEnd = dayjs.utc(shift.plan_work_end_at).diff(dayjs.utc(shift.fact_work_end_at), 'minute')
        return lateStart >= 5 || earlyEnd >= 5
    })
    return hasRedCondition ? 'warning' : 'ok'
})
const hasMajorTimeDeviation = computed(() => plannedShifts.value.some((shift) => {
    const earlyStart = shift.fact_work_start_at && dayjs.utc(shift.plan_work_start_at).diff(dayjs.utc(shift.fact_work_start_at), 'minute') >= 60
    const lateEnd = shift.fact_work_end_at && dayjs.utc(shift.fact_work_end_at).diff(dayjs.utc(shift.plan_work_end_at), 'minute') >= 60
    return Boolean(earlyStart || lateEnd)
}))
const usesCellHighlighting = computed(() => (
    store.isCellHighlighting
    && Boolean(authStore.permissions?.extended_cell_info)
    && plannedShifts.value.length > 0
))
const isOvertimeCombination = computed(() => {
    if (!usesCellHighlighting.value) return false

    if (shifts.value.length === 1) return shifts.value[0].is_overtime
    if (shifts.value.length !== 2) return false

    const overtimeShifts = shifts.value.filter((shift) => shift.is_overtime)
    if (overtimeShifts.length !== 1) return false

    const otherShift = shifts.value.find((shift) => !shift.is_overtime)
    return Boolean(
        otherShift?.day_state_document
        || (otherShift?.plan_work_start_at && otherShift?.plan_work_end_at),
    )
})
const cellHighlightClass = computed(() => {
    return isOvertimeCombination.value ? 'schedule-cell-highlight-overtime-combination' : ''
})
const cellUnderlineClass = computed(() => `schedule-cell-underline-${cellUnderlineStatus.value}`)
const dayColor = computed(() => {
    for (const status of ['warning', 'moderate']) {
        if (shifts.value.some(s => s.violation?.general_status === status)) return `bg-${status}`
    }
    return ''
})
</script>
